import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { DollarSign, Calendar, Percent, MessageCircle } from "lucide-react";

// Klarna/Stripe-inspired financing simulator on light background.
// Client-side calc using standard amortization; user drags sliders and sees monthly payment live.
export default function FinancingSimulator({ interestRate = 20, whatsapp = "5539835137", lockedPrice = null, vehicleName = "" }) {
  const [price, setPrice] = useState(lockedPrice ? Math.round(parseFloat(String(lockedPrice).replace(/[,$]/g, "")) || 568900) : 568900);
  const [downPct, setDownPct] = useState(20);
  const [months, setMonths] = useState(60);

  // Keep the price synced to the locked value if the parent updates it
  React.useEffect(() => {
    if (lockedPrice !== null && lockedPrice !== undefined) {
      const n = parseFloat(String(lockedPrice).replace(/[,$]/g, ""));
      if (!Number.isNaN(n) && n > 0) setPrice(Math.round(n));
    }
  }, [lockedPrice]);

  const isLocked = lockedPrice !== null && lockedPrice !== undefined && lockedPrice !== "";

  const calc = useMemo(() => {
    const down = (price * downPct) / 100;
    const financed = Math.max(price - down, 0);
    const r = interestRate / 100 / 12;
    const monthly = r === 0 ? financed / months : (financed * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
    const total = monthly * months;
    return { down, financed, monthly, total };
  }, [price, downPct, months, interestRate]);

  const fmt = (n) => `$${Math.round(n).toLocaleString("es-MX")}`;

  return (
    <section
      className="relative bg-[#FFFFFF] text-[#0B1623] py-16 md:py-24"
      data-testid="financing-simulator"
      aria-label="Simulador de financiamiento"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <span className="font-rajdhani font-medium text-[#3B82F6] text-xs md:text-sm tracking-[0.4em] uppercase">
              Financiamiento
            </span>
            <h2 className="font-rajdhani font-semibold text-[#0B1623] tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-6xl mt-4 whitespace-pre-line">
              {isLocked ? "Simula tu\nmensualidad." : "Calcula tu\nmensualidad."}
            </h2>
            <p className="font-manrope text-[#0B1623]/70 text-base md:text-lg mt-6 max-w-md leading-relaxed">
              {isLocked
                ? `Con base en el precio de ${vehicleName || "este vehículo"} (${fmt(price)}). Ajusta enganche y plazo para ver tu pago mensual.`
                : `Mueve los controles y observa cómo cambia tu pago mensual. Tasa referencial de ${interestRate}% anual, ajustada según tu perfil crediticio.`}
            </p>
            <a
              href={`https://api.whatsapp.com/send?phone=${whatsapp}&text=${encodeURIComponent(
                `Hola, quiero cotizar un auto de ${fmt(price)} a ${months} meses con ${downPct}% de enganche`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 bg-[#0B1623] text-white font-rajdhani font-medium tracking-[0.2em] uppercase text-sm px-8 py-4 rounded-full hover:bg-[#F09A21] transition-colors duration-300"
              data-testid="sim-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Solicitar por WhatsApp
            </a>
          </div>

          {/* Right: calculator card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl bg-white border border-[#0B1623]/8 p-6 md:p-8 shadow-[0_20px_60px_rgba(11,22,35,0.06)]"
            data-testid="sim-card"
          >
            {/* Result */}
            <div className="text-center pb-6 border-b border-[#0B1623]/8">
              <p className="font-manrope text-xs uppercase tracking-[0.3em] text-[#0B1623]/50">Mensualidad estimada</p>
              <p className="font-rajdhani font-medium text-[#0B1623] text-5xl md:text-6xl mt-2">
                {fmt(calc.monthly)}
              </p>
              <p className="font-manrope text-sm text-[#0B1623]/60 mt-2">
                a {months} meses · {downPct}% enganche
              </p>
            </div>

            {/* Sliders */}
            <div className="mt-6 space-y-6">
              {/* Price — slider hidden when locked to a specific vehicle */}
              {isLocked ? (
                <div className="rounded-xl bg-[#F5F5F5] p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-manrope text-sm text-[#0B1623]/70">
                    <DollarSign className="w-3.5 h-3.5" /> Precio del auto
                  </div>
                  <div className="text-right">
                    <p className="font-rajdhani font-semibold text-[#0B1623] text-lg leading-none">{fmt(price)}</p>
                    <p className="font-manrope text-[10px] text-[#0B1623]/50 mt-1">Fijo · según versión seleccionada</p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between font-manrope text-sm mb-2">
                    <span className="text-[#0B1623]/70 inline-flex items-center gap-2">
                      <DollarSign className="w-3.5 h-3.5" /> Precio del auto
                    </span>
                    <span className="font-semibold text-[#0B1623]">{fmt(price)}</span>
                  </div>
                  <input
                    type="range"
                    min="200000"
                    max="1500000"
                    step="10000"
                    value={price}
                    onChange={(e) => setPrice(parseInt(e.target.value))}
                    className="w-full accent-[#F09A21]"
                    data-testid="sim-price-slider"
                  />
                </div>
              )}

              {/* Down payment */}
              <div>
                <div className="flex justify-between font-manrope text-sm mb-2">
                  <span className="text-[#0B1623]/70 inline-flex items-center gap-2">
                    <Percent className="w-3.5 h-3.5" /> Enganche
                  </span>
                  <span className="font-semibold text-[#0B1623]">{downPct}% · {fmt(calc.down)}</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="50"
                  step="1"
                  value={downPct}
                  onChange={(e) => setDownPct(parseInt(e.target.value))}
                  className="w-full accent-[#F09A21]"
                  data-testid="sim-down-slider"
                />
              </div>

              {/* Term */}
              <div>
                <div className="flex justify-between font-manrope text-sm mb-2">
                  <span className="text-[#0B1623]/70 inline-flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" /> Plazo
                  </span>
                  <span className="font-semibold text-[#0B1623]">{months} meses</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[36, 48, 60, 72].map((m) => (
                    <button
                      key={m}
                      onClick={() => setMonths(m)}
                      className={`py-2 rounded-full font-rajdhani font-medium text-sm transition-colors ${
                        m === months
                          ? "bg-[#0B1623] text-white"
                          : "bg-[#0B1623]/5 text-[#0B1623] hover:bg-[#0B1623]/10"
                      }`}
                      data-testid={`sim-term-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#0B1623]/8 flex items-center justify-between font-manrope text-sm">
              <span className="text-[#0B1623]/60">Total financiado</span>
              <span className="font-semibold text-[#0B1623]">{fmt(calc.total)}</span>
            </div>
            <p className="mt-4 font-manrope text-[11px] text-[#0B1623]/55 leading-relaxed">
              Cálculo referencial usando una tasa promedio del {interestRate}% anual. Cada solicitud
              se evalúa individualmente: los perfiles con buen historial y comprobación de ingresos
              suelen obtener tasas menores; los perfiles con historial complicado pueden requerir
              una tasa ligeramente mayor. Sujeto a aprobación crediticia.
              Paga el enganche hasta el día de la entrega.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
